import { Component, OnInit } from '@angular/core'
import { collection, getDocs, query, where } from '@angular/fire/firestore'
import { Invoice, InvoiceStatus, FirestoreStructure } from '@parkupp/core'
import { AdminService } from 'src/app/services/admin.service'

interface AgingBucket {
    label: string
    count: number
    amount: number
    invoices: InvoiceDetail[]
}

interface InvoiceDetail {
    $key: string
    clientName: string
    merchantName: string
    parkingTitle: string
    amount: number
    dueDate: Date
    daysOverdue: number
}

interface MerchantPayoutDetail {
    $key: string
    merchantName: string
    unpaidInvoices: number
    unpaidAmount: number
    hasBankDetails: boolean
    oldestUnpaidDate: Date | null
}

@Component({
    selector: 'app-admin-financial-health',
    templateUrl: './admin-financial-health.component.html',
    styleUrls: ['./admin-financial-health.component.scss'],
})
export class AdminFinancialHealthComponent implements OnInit {
    isLoading = true

    // Parker Invoices Aging
    parkerInvoicesAging: AgingBucket[] = [
        { label: 'Current (0-30 days)', count: 0, amount: 0, invoices: [] },
        { label: '1-2 Months', count: 0, amount: 0, invoices: [] },
        { label: '2-3 Months', count: 0, amount: 0, invoices: [] },
        { label: '3+ Months', count: 0, amount: 0, invoices: [] },
    ]
    totalOverdueParkerInvoices = 0
    totalOverdueParkerAmount = 0

    // Merchant Payouts Aging
    merchantPayoutsAging: AgingBucket[] = [
        { label: 'Current (0-30 days)', count: 0, amount: 0, invoices: [] },
        { label: '1-2 Months', count: 0, amount: 0, invoices: [] },
        { label: '2-3 Months', count: 0, amount: 0, invoices: [] },
        { label: '3+ Months', count: 0, amount: 0, invoices: [] },
    ]
    totalUnpaidMerchantPayouts = 0
    totalUnpaidMerchantAmount = 0

    // Merchant Details
    merchantPayoutDetails: MerchantPayoutDetail[] = []

    constructor(private adminSvc: AdminService) {}

    ngOnInit(): void {
        this.loadFinancialHealthData()
    }

    async loadFinancialHealthData() {
        this.isLoading = true

        try {
            await Promise.all([this.loadParkerInvoicesAging(), this.loadMerchantPayoutsAging()])
        } catch (error) {
            console.error('[Financial Health] Error loading data:', error)
        } finally {
            this.isLoading = false
        }
    }

    /**
     * Load parker invoices that are overdue (not paid to us)
     */
    async loadParkerInvoicesAging() {
        // Reset
        this.parkerInvoicesAging.forEach((bucket) => {
            bucket.count = 0
            bucket.amount = 0
            bucket.invoices = []
        })
        this.totalOverdueParkerInvoices = 0
        this.totalOverdueParkerAmount = 0

        const today = new Date()

        // Get all DUE invoices (not paid)
        const invoicesRef = query(collection(this.adminSvc.firestore, FirestoreStructure.INVOICES), where('status', '==', InvoiceStatus.DUE))

        const snapshot = await getDocs(invoicesRef)

        snapshot.docs.forEach((doc) => {
            const data = doc.data()
            const invoice = new Invoice(data, doc.id)

            // Calculate days overdue
            let dueDate: Date
            if (invoice.dueDate) {
                dueDate = invoice.dueDate instanceof Date ? invoice.dueDate : (invoice.dueDate as any).toDate?.() || new Date((invoice.dueDate as any).seconds * 1000)
            } else {
                dueDate = new Date()
            }

            const daysOverdue = Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24))

            // Only count if actually overdue (past due date)
            if (daysOverdue < 0) return

            const detail: InvoiceDetail = {
                $key: doc.id,
                clientName: `${invoice.denormClientFirstName || ''} ${invoice.denormClientLastName || ''}`.trim() || 'Unknown',
                merchantName: invoice.denormMerchantCompanyName || 'Unknown',
                parkingTitle: invoice.denormParkingTitle || 'Unknown',
                amount: invoice.amountTotalDue || 0,
                dueDate: dueDate,
                daysOverdue: daysOverdue,
            }

            // Categorize into buckets
            if (daysOverdue <= 30) {
                this.parkerInvoicesAging[0].count++
                this.parkerInvoicesAging[0].amount += detail.amount
                this.parkerInvoicesAging[0].invoices.push(detail)
            } else if (daysOverdue <= 60) {
                this.parkerInvoicesAging[1].count++
                this.parkerInvoicesAging[1].amount += detail.amount
                this.parkerInvoicesAging[1].invoices.push(detail)
            } else if (daysOverdue <= 90) {
                this.parkerInvoicesAging[2].count++
                this.parkerInvoicesAging[2].amount += detail.amount
                this.parkerInvoicesAging[2].invoices.push(detail)
            } else {
                this.parkerInvoicesAging[3].count++
                this.parkerInvoicesAging[3].amount += detail.amount
                this.parkerInvoicesAging[3].invoices.push(detail)
            }

            this.totalOverdueParkerInvoices++
            this.totalOverdueParkerAmount += detail.amount
        })

        // Sort invoices in each bucket by days overdue (descending)
        this.parkerInvoicesAging.forEach((bucket) => {
            bucket.invoices.sort((a, b) => b.daysOverdue - a.daysOverdue)
        })
    }

    /**
     * Load merchant payouts that haven't been paid out
     */
    async loadMerchantPayoutsAging() {
        // Reset
        this.merchantPayoutsAging.forEach((bucket) => {
            bucket.count = 0
            bucket.amount = 0
            bucket.invoices = []
        })
        this.totalUnpaidMerchantPayouts = 0
        this.totalUnpaidMerchantAmount = 0
        this.merchantPayoutDetails = []

        const today = new Date()

        // Get all PAID invoices that haven't been paid out to merchants
        const invoicesRef = query(collection(this.adminSvc.firestore, FirestoreStructure.INVOICES), where('status', '==', InvoiceStatus.PAID), where('paidOut', '==', false))

        const snapshot = await getDocs(invoicesRef)

        // Group by merchant
        const merchantMap = new Map<string, MerchantPayoutDetail>()

        snapshot.docs.forEach((doc) => {
            const data = doc.data()
            const invoice = new Invoice(data, doc.id)

            // Calculate the payout amount (amount minus commission)
            const payoutAmount = invoice.periodFinalPayout || invoice.amountTotalDue * 0.85 || 0

            // Get payment date for aging
            let paymentDate: Date
            if (invoice.paymentDate) {
                paymentDate = invoice.paymentDate instanceof Date ? invoice.paymentDate : (invoice.paymentDate as any).toDate?.() || new Date((invoice.paymentDate as any).seconds * 1000)
            } else if (invoice.createdAt) {
                paymentDate = invoice.createdAt instanceof Date ? invoice.createdAt : (invoice.createdAt as any).toDate?.() || new Date((invoice.createdAt as any).seconds * 1000)
            } else {
                paymentDate = new Date()
            }

            const daysUnpaid = Math.floor((today.getTime() - paymentDate.getTime()) / (1000 * 60 * 60 * 24))

            const detail: InvoiceDetail = {
                $key: doc.id,
                clientName: `${invoice.denormClientFirstName || ''} ${invoice.denormClientLastName || ''}`.trim() || 'Unknown',
                merchantName: invoice.denormMerchantCompanyName || 'Unknown',
                parkingTitle: invoice.denormParkingTitle || 'Unknown',
                amount: payoutAmount,
                dueDate: paymentDate,
                daysOverdue: daysUnpaid,
            }

            // Categorize into buckets
            if (daysUnpaid <= 30) {
                this.merchantPayoutsAging[0].count++
                this.merchantPayoutsAging[0].amount += payoutAmount
                this.merchantPayoutsAging[0].invoices.push(detail)
            } else if (daysUnpaid <= 60) {
                this.merchantPayoutsAging[1].count++
                this.merchantPayoutsAging[1].amount += payoutAmount
                this.merchantPayoutsAging[1].invoices.push(detail)
            } else if (daysUnpaid <= 90) {
                this.merchantPayoutsAging[2].count++
                this.merchantPayoutsAging[2].amount += payoutAmount
                this.merchantPayoutsAging[2].invoices.push(detail)
            } else {
                this.merchantPayoutsAging[3].count++
                this.merchantPayoutsAging[3].amount += payoutAmount
                this.merchantPayoutsAging[3].invoices.push(detail)
            }

            this.totalUnpaidMerchantPayouts++
            this.totalUnpaidMerchantAmount += payoutAmount

            // Track by merchant
            const merchantKey = invoice.merchantRef?.id || 'unknown'
            if (!merchantMap.has(merchantKey)) {
                merchantMap.set(merchantKey, {
                    $key: merchantKey,
                    merchantName: invoice.denormMerchantCompanyName || 'Unknown',
                    unpaidInvoices: 0,
                    unpaidAmount: 0,
                    hasBankDetails: true, // Will be updated later
                    oldestUnpaidDate: null,
                })
            }

            const merchantDetail = merchantMap.get(merchantKey)!
            merchantDetail.unpaidInvoices++
            merchantDetail.unpaidAmount += payoutAmount

            if (!merchantDetail.oldestUnpaidDate || paymentDate < merchantDetail.oldestUnpaidDate) {
                merchantDetail.oldestUnpaidDate = paymentDate
            }
        })

        // Sort invoices in each bucket by days unpaid (descending)
        this.merchantPayoutsAging.forEach((bucket) => {
            bucket.invoices.sort((a, b) => b.daysOverdue - a.daysOverdue)
        })

        // Convert map to array and sort by unpaid amount
        this.merchantPayoutDetails = Array.from(merchantMap.values()).sort((a, b) => b.unpaidAmount - a.unpaidAmount)
    }

    /**
     * Format currency for display
     */
    formatCurrency(value: number): string {
        if (value >= 1000000) {
            return (value / 1000000).toFixed(1) + 'M'
        } else if (value >= 1000) {
            return (value / 1000).toFixed(1) + 'K'
        }
        return value.toFixed(0)
    }

    /**
     * Toggle bucket expansion
     */
    expandedBuckets: Set<string> = new Set()

    toggleBucket(type: string, index: number) {
        const key = `${type}-${index}`
        if (this.expandedBuckets.has(key)) {
            this.expandedBuckets.delete(key)
        } else {
            this.expandedBuckets.add(key)
        }
    }

    isBucketExpanded(type: string, index: number): boolean {
        return this.expandedBuckets.has(`${type}-${index}`)
    }
}
